import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, DollarSign, Download, Loader2, FileText, Car, MapPin } from 'lucide-react';
import {
  BulkMvrResponse,
  BulkMvrResultItem,
  MvrTitleRecord,
  MvrRegistrationRecord,
  formatMvrDate,
  formatOdometer
} from '../services/mvrService';

interface BulkMVRResultsProps {
  results: BulkMvrResponse;
  showPricing?: boolean;
  onGeneratePdf: () => void;
  isGeneratingPdf: boolean;
}

const BulkMVRResults: React.FC<BulkMVRResultsProps> = ({
  results,
  showPricing = true,
  onGeneratePdf,
  isGeneratingPdf,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const successfulResults = results.results.filter(r => r.success);

  return (
    <div className="space-y-4">
      {/* Summary Banner */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-black/[0.08] p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-[#64748b]">Total:</span>
              <span className="font-bold text-[#0f172a]">{results.totalRequested}</span>
            </div>
            <div className="flex items-center gap-2 text-[#16a34a]">
              <CheckCircle2 className="w-4 h-4" />
              <span>{results.totalSucceeded} succeeded</span>
            </div>
            {results.totalFailed > 0 && (
              <div className="flex items-center gap-2 text-[#dc2626]">
                <XCircle className="w-4 h-4" />
                <span>{results.totalFailed} failed</span>
              </div>
            )}
            {showPricing && results.totalBillingAmount > 0 && (
              <div className="flex items-center gap-2 text-[#d97706]">
                <DollarSign className="w-4 h-4" />
                <span>{formatCurrency(results.totalBillingAmount)}</span>
              </div>
            )}
          </div>

          {/* Generate PDF Button */}
          {successfulResults.length > 0 && (
            <button
              onClick={onGeneratePdf}
              disabled={isGeneratingPdf}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] text-white text-sm font-bold rounded-lg transition-all shadow-lg shadow-[#c41e3a]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Generate Combined PDF</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Pricing Breakdown Table */}
      {showPricing && results.pricingBreakdown && results.pricingBreakdown.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-black/[0.08] shadow-sm overflow-hidden">
          <div className="p-4 border-b border-black/[0.06]">
            <h3 className="text-sm font-medium text-[#0f172a]">Pricing Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f8fafc] border-b border-black/[0.06]">
                <tr>
                  <th className="px-4 py-2 text-left text-[#64748b] font-medium">State</th>
                  <th className="px-4 py-2 text-left text-[#64748b] font-medium">Type</th>
                  <th className="px-4 py-2 text-right text-[#64748b] font-medium">Qty</th>
                  <th className="px-4 py-2 text-right text-[#64748b] font-medium">Unit</th>
                  <th className="px-4 py-2 text-right text-[#64748b] font-medium">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/[0.06]">
                {results.pricingBreakdown.map((group, idx) => (
                  <tr key={idx} className="hover:bg-black/[0.02]">
                    <td className="px-4 py-2 text-[#0f172a]">{group.stateName}</td>
                    <td className="px-4 py-2 text-[#64748b]">{group.recordType}</td>
                    <td className="px-4 py-2 text-right text-[#0f172a]">{group.count}</td>
                    <td className="px-4 py-2 text-right text-[#64748b]">{formatCurrency(group.unitCost)}</td>
                    <td className="px-4 py-2 text-right text-[#0f172a] font-medium">{formatCurrency(group.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#f8fafc] border-t border-black/[0.06]">
                <tr>
                  <td colSpan={4} className="px-4 py-2 text-right text-[#0f172a] font-medium">Total</td>
                  <td className="px-4 py-2 text-right text-[#0f172a] font-bold">{formatCurrency(results.totalBillingAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Results List */}
      <div className="space-y-3">
        {results.results.map((item, index) => (
          <ResultItem
            key={index}
            item={item}
            isExpanded={expandedItems.has(index)}
            onToggle={() => toggleExpanded(index)}
            showPricing={showPricing}
          />
        ))}
      </div>
    </div>
  );
};

interface ResultItemProps {
  item: BulkMvrResultItem;
  isExpanded: boolean;
  onToggle: () => void;
  showPricing?: boolean;
}

const ResultItem: React.FC<ResultItemProps> = ({
  item,
  isExpanded,
  onToggle,
  showPricing = true,
}) => {
  // Extract vehicle description from first available title or registration record
  let vehicleDescription = '';
  if (item.titleResponse?.titles && item.titleResponse.titles.length > 0) {
    const title = item.titleResponse.titles[0];
    vehicleDescription = [title.year, title.make, title.model]
      .filter(Boolean)
      .join(' ');
  } else if (item.registrationResponse?.registrations && item.registrationResponse.registrations.length > 0) {
    const reg = item.registrationResponse.registrations[0];
    vehicleDescription = [reg.year, reg.make, reg.model]
      .filter(Boolean)
      .join(' ');
  }

  return (
    <div className={`bg-white/80 backdrop-blur-sm rounded-xl border overflow-hidden transition-all shadow-sm ${
      item.success ? 'border-black/[0.08]' : 'border-[#dc2626]/30'
    }`}>
      {/* Header Row */}
      <button
        onClick={onToggle}
        disabled={!item.success}
        className={`w-full px-4 py-3 flex items-center justify-between text-left transition-all ${
          item.success
            ? 'hover:bg-black/[0.02] cursor-pointer'
            : 'cursor-default'
        }`}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Index Number */}
          <span className="text-[#94a3b8] text-sm font-medium w-6 text-center flex-shrink-0">
            {item.index + 1}.
          </span>

          {/* VIN */}
          <span className="font-mono text-sm text-[#0f172a] font-medium">
            {item.vin}
          </span>

          {/* State Badge */}
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded flex-shrink-0">
            {item.stateCode}
          </span>

          {/* Record Type Badge */}
          <span className={`px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${
            item.recordType.toLowerCase() === 'title'
              ? 'bg-purple-100 text-purple-700'
              : item.recordType.toLowerCase() === 'registration'
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-indigo-100 text-indigo-700'
          }`}>
            {item.recordType}
          </span>

          {/* Vehicle Description */}
          {vehicleDescription && (
            <span className="text-sm text-[#64748b] truncate hidden sm:block">
              {vehicleDescription}
            </span>
          )}

          {/* Reference Note */}
          {item.referenceNote && (
            <span className="text-xs text-[#94a3b8] truncate hidden md:block">
              Ref: {item.referenceNote}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Status Badge */}
          {item.success ? (
            <div className="flex items-center gap-2">
              {showPricing && item.billingAmount != null && item.billingAmount > 0 && (
                <span className="px-2 py-1 bg-[#d97706]/10 text-[#d97706] text-xs font-medium rounded">
                  ${item.billingAmount.toFixed(2)}
                </span>
              )}
              <span className="px-2 py-1 bg-[#16a34a]/10 text-[#16a34a] text-xs font-medium rounded">
                Success
              </span>
              {/* Expand/Collapse Indicator */}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-[#94a3b8]" />
              ) : (
                <ChevronDown className="w-4 h-4 text-[#94a3b8]" />
              )}
            </div>
          ) : (
            <span className="px-2 py-1 bg-[#dc2626]/10 text-[#dc2626] text-xs font-medium rounded flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              Failed
            </span>
          )}
        </div>
      </button>

      {/* Error Message for Failed Items */}
      {!item.success && item.errorMessage && (
        <div className="px-4 pb-3 text-sm text-[#dc2626]">
          {item.errorMessage}
        </div>
      )}

      {/* Expanded Content for Successful Items */}
      {item.success && isExpanded && (
        <div className="border-t border-black/[0.06] p-4 space-y-4">
          {/* Title Records Summary */}
          {item.titleResponse?.titles && item.titleResponse.titles.length > 0 && (
            <TitleSummary titles={item.titleResponse.titles} />
          )}

          {/* Registration Records Summary */}
          {item.registrationResponse?.registrations && item.registrationResponse.registrations.length > 0 && (
            <RegistrationSummary registrations={item.registrationResponse.registrations} />
          )}
        </div>
      )}
    </div>
  );
};

interface TitleSummaryProps {
  titles: MvrTitleRecord[];
}

const TitleSummary: React.FC<TitleSummaryProps> = ({ titles }) => {
  return (
    <div className="bg-[#f8fafc] rounded-lg p-4 border border-black/[0.05]">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#64748b]" />
        <h4 className="text-sm font-medium text-[#0f172a]">
          Title Records ({titles.length})
        </h4>
      </div>
      <div className="space-y-3">
        {titles.map((title, idx) => (
          <div key={idx} className="bg-white rounded-lg p-3 space-y-2">
            {/* Title Number and Status */}
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1">
                {title.titleNumber && (
                  <div className="text-sm text-[#0f172a] font-medium">
                    Title: {title.titleNumber}
                  </div>
                )}
                {title.status && (
                  <div className="text-xs text-[#64748b]">
                    Status: {title.status}
                  </div>
                )}
              </div>
              {title.titleIssueDate && (
                <div className="text-xs text-[#94a3b8]">
                  {formatMvrDate(title.titleIssueDate)}
                </div>
              )}
            </div>

            {/* Vehicle Info */}
            {(title.year || title.make || title.model) && (
              <div className="flex items-center gap-2 text-sm">
                <Car className="w-3.5 h-3.5 text-[#94a3b8]" />
                <span className="text-[#64748b]">
                  {[title.year, title.make, title.model, title.body, title.color]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </div>
            )}

            {/* Owner Info */}
            {title.owners && title.owners.length > 0 && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-3.5 h-3.5 text-[#94a3b8] mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  {title.owners.map((owner, oidx) => (
                    <div key={oidx} className="text-[#64748b]">
                      <div className="font-medium text-[#0f172a]">{owner.name}</div>
                      {owner.addressLine1 && (
                        <div className="text-xs">
                          {[owner.addressLine1, owner.city, owner.state, owner.zip]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lienholder Info */}
            {title.lienHolders && title.lienHolders.length > 0 && (
              <div className="pt-2 border-t border-black/[0.06]">
                <div className="text-xs text-[#64748b] mb-1">Lienholder:</div>
                <div className="space-y-1">
                  {title.lienHolders.map((lien, lidx) => {
                    const addressParts = [lien.addressLine1];
                    if (lien.city) addressParts.push(lien.city);
                    if (lien.state || lien.zip) {
                      addressParts.push([lien.state, lien.zip].filter(Boolean).join(' '));
                    }
                    const formattedAddress = addressParts.filter(Boolean).join(', ');
                    return (
                      <div key={lidx} className="text-sm">
                        <div className="font-medium text-[#0f172a]">{lien.name}</div>
                        {formattedAddress && (
                          <div className="text-xs text-[#64748b]">{formattedAddress}</div>
                        )}
                        {lien.lienDate && (
                          <div className="text-xs text-[#94a3b8]">
                            Lien Date: {formatMvrDate(lien.lienDate)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Odometer */}
            {title.odometerReading && (
              <div className="text-xs text-[#64748b]">
                Odometer: {formatOdometer(title.odometerReading)}
                {title.odometerStatus && ` (${title.odometerStatus})`}
              </div>
            )}

            {/* Brands */}
            {title.brands && title.brands.length > 0 && (
              <div className="pt-2 border-t border-black/[0.06]">
                <div className="flex flex-wrap gap-1">
                  {title.brands.map((brand, bidx) => (
                    <span
                      key={bidx}
                      className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface RegistrationSummaryProps {
  registrations: MvrRegistrationRecord[];
}

const RegistrationSummary: React.FC<RegistrationSummaryProps> = ({ registrations }) => {
  return (
    <div className="bg-[#f8fafc] rounded-lg p-4 border border-black/[0.05]">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#64748b]" />
        <h4 className="text-sm font-medium text-[#0f172a]">
          Registration Records ({registrations.length})
        </h4>
      </div>
      <div className="space-y-3">
        {registrations.map((reg, idx) => (
          <div key={idx} className="bg-white rounded-lg p-3 space-y-2">
            {/* Plate and Status */}
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1">
                {reg.plate && (
                  <div className="text-sm text-[#0f172a] font-medium">
                    Plate: {reg.plate}
                    {reg.plateType && ` (${reg.plateType})`}
                  </div>
                )}
                {reg.status && (
                  <div className="text-xs text-[#64748b]">
                    Status: {reg.status}
                  </div>
                )}
              </div>
              {reg.expirationDate && (
                <div className="text-xs text-[#94a3b8]">
                  Expires: {formatMvrDate(reg.expirationDate)}
                </div>
              )}
            </div>

            {/* Vehicle Info */}
            {(reg.year || reg.make || reg.model) && (
              <div className="flex items-center gap-2 text-sm">
                <Car className="w-3.5 h-3.5 text-[#94a3b8]" />
                <span className="text-[#64748b]">
                  {[reg.year, reg.make, reg.model, reg.body, reg.color]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </div>
            )}

            {/* Owner Info */}
            {reg.owners && reg.owners.length > 0 && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-3.5 h-3.5 text-[#94a3b8] mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  {reg.owners.map((owner, oidx) => (
                    <div key={oidx} className="text-[#64748b]">
                      <div className="font-medium text-[#0f172a]">{owner.name}</div>
                      {owner.addressLine1 && (
                        <div className="text-xs">
                          {[owner.addressLine1, owner.city, owner.state, owner.zip]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Registration Type and Issue Date */}
            <div className="flex flex-wrap gap-3 text-xs text-[#64748b]">
              {reg.issueDate && (
                <div>Issued: {formatMvrDate(reg.issueDate)}</div>
              )}
              {reg.registrationType && (
                <div>Type: {reg.registrationType}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BulkMVRResults;
